import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import LinkIcon from 'icons/LinkIcon';
import axiosPrivate from 'config/axiosPrivate';
import { setCurrentRedaction } from 'store/AdminClient/RedactionData';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import { UserSessionSelector } from 'store/UserSession';
import { DownloadTextWordType } from 'components/shared/DownloadFile/DownloadTextWordType';
import { ExcelExportButton } from 'components/shared/ExcelExport/ExcelExportButton';
import { setAISmartContentType } from 'store/AdminClient/AISmartContentType';
import { StatusDropdown } from 'components/shared/StatusDropdown/StatusDropdown';
import TablePagination from 'components/shared/Pagination/TablePagination';
import DataTable from 'components/shared/DataTable';
import OrderTypeFilter from 'components/shared/Filter/OrderTypeFilter';
import '../../components/shared/shared.css';
import { formatDateShort } from 'utils/DateUtils';
import './Listofwebsitetexts.css';
import usePageTracking from 'utils/hooks/usePageTracking';

const ADMIN_CLIENT_WEBSITE_TEXTS_PAGE = '/AdminClient/allcontent';

export default function Listofwebsitetexts() {
  usePageTracking(ADMIN_CLIENT_WEBSITE_TEXTS_PAGE);

  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [websiteId, setWebsiteId] = useState(null);
  const lastWebsiteIdRef = useRef(null);
  const STATUS_FILTER_LIST = useMemo(
    () => [
      { type: 'ALL', name: t('Tous les textes'), text: 'Tous les textes' },
      { type: 'VALIDATED', name: t('Textes validés'), text: 'Textes validés' },
      { type: 'DELIVERED', name: t('Textes livrés'), text: 'Textes livrés' },
      { type: 'DRAFT', name: t('Brouillons'), text: 'Brouillons' },
      { type: 'ONLINE', name: t('En ligne'), text: 'En ligne' },
    ],
    [t]
  );
  const CONTENT_TYPE_FILTER_LIST = useMemo(
    () => [
      { type: 'ALL', name: t('Tous les types'), text: 'Tous les types' },
      { type: 'PRODUCT', name: t('Fiche produit'), text: 'Fiche produit' },
      { type: 'LONG_CATEGORY', name: t('Page catégorie'), text: 'Page catégorie' },
      { type: 'GMB', name: t('Fiche GMB'), text: 'Fiche GMB' },
      { type: 'STORE_LOCATOR', name: t('Store locator'), text: 'Store locator' },
      { type: 'NON_TRANSLATED', name: t('Rédaction'), text: 'Rédaction' },
      { type: 'TRANSLATED', name: t('Traduction'), text: 'Traduction' },
    ],
    [t]
  );
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
  const [selectedContentTypeFilter, setSelectedContentTypeFilter] = useState(null);
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState(null);
  const [contentTypeFilterAnchorEl, setContentTypeFilterAnchorEl] = useState(null);
  const [openActionsFor, setOpenActionsFor] = useState(null);
  const [actionsMenuCoords, setActionsMenuCoords] = useState({ top: 0, left: 0 });
  const [confirmation, setConfirmation] = useState(false);
  const [confirmationMsg, setConfirmationMsg] = useState('');
  const [textToDelete, setTextToDelete] = useState(null);
  const statusOptions = useMemo(() => ([
    {
      value: 'Brouillon',
      label: t('Brouillon'),
      variant: 'draft',
      flags: { delivered: false, validated: false, deployed: false, inRevision: false },
    },
    {
      value: 'Livré',
      label: t('Livré'),
      variant: 'delivered',
      flags: { delivered: true, validated: false, deployed: false, inRevision: false },
    },
    {
      value: 'En révision',
      label: t('En révision'),
      variant: 'revision',
      flags: { delivered: false, validated: false, deployed: false, inRevision: true },
    },
    {
      value: 'Validé',
      label: t('Validé'),
      variant: 'validated',
      flags: { delivered: true, validated: true, deployed: false, inRevision: false },
    },
    {
      value: 'En ligne',
      label: t('En ligne'),
      variant: 'online',
      flags: { delivered: true, validated: true, deployed: true, inRevision: false },
    },
  ]), [t]);

  const resolveTextStatus = (text) => {
    if (text?.deployed) return 'En ligne';
    if (text?.validated) return 'Validé';
    if (text?.inRevision) return 'En révision';
    if (text?.delivered) return 'Livré';
    return 'Brouillon';
  };

  // Website filter — ne pas utiliser clients = [] par défaut : null = pas encore chargé depuis Redux
  const [selectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');
  const { clients: clientsFromSelector, isLoadingClients = false } =
    useSelector(adminClientClientListSelector) || {};
  const clients = Array.isArray(clientsFromSelector) ? clientsFromSelector : null;
  const clientsList = clients ?? [];
  const hasNoWebsiteConfigured =
    clients !== null && !isLoadingClients && clientsList.length === 0;
  const { user } = useSelector(UserSessionSelector) || {};

  const handleGoToAddWebsite = () => {
    history.push('/AdminClient/AjouterSiteWeb');
  };

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      return formatDateShort(value);
    } catch (e) {
      return String(value);
    }
  };

  const getContentTypeLabel = (contentType, type) => {

    if (contentType) {
      const contentTypeMap = {
        'PRODUCT': t('Fiche produit'),
        'LONG_CATEGORY': t('Page catégorie'),
        'GMB': t('Fiche GMB'),
        'STORE_LOCATOR': t('Store locator'),
      };
      return contentTypeMap[contentType] || '-';
    }

    // Si contentType est null, utiliser type
    if (type) {
      const typeMap = {
        'NON_TRANSLATED': t('Rédaction'),
        'TRANSLATED': t('Traduction'),
      };
      return typeMap[type] || '-';
    }

    return '';
  };

  const handleOptimisticUpdate = (textId, nextStatus) => {
    const nextOption = statusOptions.find((opt) => opt.value === nextStatus);

    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== textId) return it;
        return {
          ...it,
          status: nextStatus,
          delivered: nextOption?.flags?.delivered ?? it.delivered,
          validated: nextOption?.flags?.validated ?? it.validated,
          deployed: nextOption?.flags?.deployed ?? it.deployed,
          inRevision: nextOption?.flags?.inRevision ?? false,
        };
      })
    );
  };

  const handleStatusChange = (textId, nextStatus) => {

  };

  const handleStatusChangeError = (error, previousItem) => {
    // Rollback en cas d'erreur
    if (previousItem) {
      setItems((prev) =>
        prev.map((it) => {
          if (it.id === previousItem.id) return previousItem;
          return it;
        })
      );
    }
    alert(t('Erreur lors de la mise à jour du statut. Veuillez réessayer.'));
  };

  useEffect(() => {
    setSelectedStatusFilter(STATUS_FILTER_LIST[0]);
  }, [STATUS_FILTER_LIST]);

  useEffect(() => {
    setSelectedContentTypeFilter(CONTENT_TYPE_FILTER_LIST[0]);
  }, [CONTENT_TYPE_FILTER_LIST]);

  const handleStatusFilterClick = (idx, e) => {
    setStatusFilterAnchorEl({ [idx]: e.currentTarget });
  };

  const handleStatusFilterClose = () => {
    setStatusFilterAnchorEl(null);
  };

  const handleContentTypeFilterClick = (idx, e) => {
    setContentTypeFilterAnchorEl({ [idx]: e.currentTarget });
  };

  const handleContentTypeFilterClose = () => {
    setContentTypeFilterAnchorEl(null);
  };

  const handleTextClick = async (item) => {
    try {
      if (item.contentType) {
        localStorage.setItem('AdminClient_TextId', item.id);
        dispatch(setAISmartContentType(item.contentType));
        history.push(`/AdminClient/createIAsmarttext/${item.id}`);
        return;
      }

      let textDetails = null;
      try {
        const res = await axiosPrivate.get(`/texts/external/${item.id}/fullDetails`);
        textDetails = res?.data || null;
      } catch (e) {
        console.error('Erreur lors du chargement des détails du texte:', e);
      }

      const normalizedType = String(textDetails?.type || item?.type || '');
      const projectId =
        textDetails?.projectId ??

        null;
      if (normalizedType === 'TRANSLATED' && !projectId) {
        localStorage.setItem('AdminClient_TextId', item.id);
        history.push(`/AdminClient/CreateTraductionIA/${item.id}`);
        return;
      }

      dispatch(
        setCurrentRedaction({
          id: (textDetails && textDetails.id) || item.id,
          type: (textDetails && textDetails.type) || item.type || '',
          textType: (textDetails && textDetails.type) || item.type || '',
          title: (textDetails && (textDetails.textTitle || textDetails.keyword)) || item.keyword || '',
          textTitle: (textDetails && (textDetails.textTitle || textDetails.keyword)) || item.keyword || '',
          keyword: (textDetails && textDetails.keyword) || item.keyword || '',
          url: (textDetails && textDetails.url) || item.url || '',
          titleTag: (textDetails && textDetails.titleTag) || '',
          metaDescriptionTag: (textDetails && textDetails.metaDescriptionTag) || '',
          initialHtmlContent: (textDetails && textDetails.initialHtmlContent) || '',
          initialTextContent: (textDetails && textDetails.initialTextContent) || '',
          actualHtmlContent: (textDetails && textDetails.actualHtmlContent) || '',
          actualTextContent: (textDetails && textDetails.actualTextContent) || '',
          semanticGap: (textDetails && textDetails.semanticGap) || [],
          analyseResult: (textDetails && textDetails.analyseResult) || null,
          stuffedKeywords: (textDetails && textDetails.stuffedKeywords) || [],
          internalLinking: (textDetails && textDetails.internalLinking) || '',
          numberWords: Number(textDetails?.numberWords) || 0,
          axeRedaction: (textDetails && (textDetails.axeRedaction || textDetails.writingAxis)) || '',
          retranscription: (textDetails && textDetails.retranscription) || '',
          delivered: !!(textDetails && textDetails.delivered),
          revisionSent: !!(textDetails && textDetails.revisionSent),
          revisionDone: !!(textDetails && textDetails.revisionDone),
          globalRevisionSent: !!(textDetails && textDetails.globalRevisionSent),
          validated: !!(textDetails && textDetails.validated),
          orderId: (textDetails && textDetails.projectId) || null,
          projectId: (textDetails && textDetails.projectId) || null,
          createdAt: (textDetails && textDetails.createdAt) || item.addedAt || new Date().toISOString(),
          initialScoreData: (textDetails && textDetails.initialScoreData) || null,
          contentScore: (textDetails && textDetails.contentScore) || null,
        })
      );

      const textId = (textDetails && textDetails.id) || item.id;
      try {
        if (textId) localStorage.setItem('AdminClient_TextId', String(textId));
      } catch (e) { }
      history.push(textId ? `/AdminClient/TextDetails/${textId}` : '/AdminClient/TextDetails');
    } catch (e) {
      console.error('Erreur lors de la navigation vers les détails:', e);
    }
  };

  const handleDeleteClick = (item) => {
    setTextToDelete(item);
    setConfirmationMsg(t('Êtes-vous sûr de vouloir supprimer ce contenu ?'));
    setConfirmation(true);
  };

  const handleActionsClick = (e, rowId) => {
    e.stopPropagation();
    if (openActionsFor === rowId) {
      setOpenActionsFor(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const estimatedHeight = 90;
    const menuWidth = 190;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    let top = rect.bottom + 4;
    if (top + estimatedHeight > viewportHeight) {
      top = rect.top - estimatedHeight - 4;
    }

    let left = rect.left;
    if (left + menuWidth > viewportWidth - 8) {
      left = viewportWidth - menuWidth - 8;
    }
    if (left < 8) left = 8;

    setActionsMenuCoords({ top, left });
    setOpenActionsFor(rowId);
  };

  const handleDeleteConfirm = async () => {
    if (!textToDelete || !textToDelete.id) return;

    try {
      // Si contentType existe, utiliser l'API unassigned, sinon l'API standard
      const deleteUrl = textToDelete.contentType
        ? `/texts/external/unassigned/${textToDelete.id}/delete`
        : `/texts/external/${textToDelete.id}/delete`;

      await axiosPrivate.delete(deleteUrl);
      setItems((prev) => prev.filter((it) => it.id !== textToDelete.id));
      setConfirmation(false);
      setTextToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(t('Erreur lors de la suppression du contenu'));
      setConfirmation(false);
      setTextToDelete(null);
    }
  };
  
  const getFilteredDataForExport = () => {
    const matchesSearch = (it) =>
      [it.keyword, it.url, it.status]
        .join(' ')
        .toLowerCase()
        .includes((searchTerm || '').toLowerCase());
    const matchesStatus = (it) => {
      switch (selectedStatusFilter?.type) {
        case 'VALIDATED':
          return it.status === 'Validé';
        case 'DELIVERED':
          return it.status === 'Livré';
        case 'DRAFT':
          return it.status === 'Brouillon';
        case 'ONLINE':
          return it.status === 'En ligne';
        case 'ALL':
        default:
          return true;
      }
    };
    const matchesContentType = (it) => {
      const v = selectedContentTypeFilter?.type;
      if (!v || v === 'ALL') return true;
      const hasContentType = it.contentType !== null && it.contentType !== undefined && String(it.contentType) !== '';
      if (v === 'NON_TRANSLATED') {
        return it.type === 'NON_TRANSLATED' && !hasContentType;
      }
      if (v === 'TRANSLATED') {
        return it.type === 'TRANSLATED' && !hasContentType;
      }
      return it.contentType === v;
    };
    return (items || []).filter((it) => matchesSearch(it) && matchesStatus(it) && matchesContentType(it));
  };

  // Colonnes pour l'export Excel
  const exportColumns = [
    { label: t('Mot clé'), value: 'keyword' },
    { label: t('Type de contenu'), value: (item) => getContentTypeLabel(item.contentType, item.type) },
    { label: t('Date d\'ajout'), value: (item) => formatDate(item.addedAt) },
    { label: t('URL'), value: 'url' },
    { label: t('Status'), value: 'status' },
    { label: t('Livraison'), value: (item) => formatDate(item.deliveryDate) },
    { label: t('Validation'), value: (item) => formatDate(item.validationDate) },
    { label: t('Déploiement'), value: (item) => formatDate(item.deploymentDate) },
  ];

  const filteredItems = useMemo(() => {
    const q = (searchTerm || '').toLowerCase();
    return (items || []).filter((it) => {
      const matchesSearch = [it.keyword, it.url, it.status].join(' ').toLowerCase().includes(q);
      const matchesStatus = (() => {
        switch (selectedStatusFilter?.type) {
          case 'VALIDATED':
            return it.status === 'Validé';
          case 'DELIVERED':
            return it.status === 'Livré';
          case 'DRAFT':
            return it.status === 'Brouillon';
          case 'ONLINE':
            return it.status === 'En ligne';
          case 'ALL':
          default:
            return true;
        }
      })();
      const matchesContentType = (() => {
        const v = selectedContentTypeFilter?.type;
        if (!v || v === 'ALL') return true;
        const hasContentType = it.contentType !== null && it.contentType !== undefined && String(it.contentType) !== '';
        if (v === 'NON_TRANSLATED') return it.type === 'NON_TRANSLATED' && !hasContentType;
        if (v === 'TRANSLATED') return it.type === 'TRANSLATED' && !hasContentType;
        return it.contentType === v;
      })();
      return matchesSearch && matchesStatus && matchesContentType;
    });
  }, [items, searchTerm, selectedStatusFilter, selectedContentTypeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedItems = useMemo(() => {
    const start = (safeCurrentPage - 1) * itemsPerPage;
    return filteredItems.slice(start, start + itemsPerPage);
  }, [filteredItems, safeCurrentPage, itemsPerPage]);

  const columns = useMemo(() => ([
    {
      header: t('Mot clé'),
      accessor: 'keyword',
      width: '28%',
      renderCell: (it) => (
        <span
          title={it.keyword}
          style={{
            display: 'inline-block',
            maxWidth: 520,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            color: '#111827',
            fontWeight: 500,
          }}
        >
          {it.keyword}
        </span>
      ),
    },
    {
      header: t('Type de contenu'),
      accessor: 'contentType',
      width: '11%',
      renderHeader: () => (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span>{t('Type de contenu')}</span>
          <OrderTypeFilter
            orderTypeList={CONTENT_TYPE_FILTER_LIST}
            selectedType={selectedContentTypeFilter}
            setSelectedType={(opt) => {
              setSelectedContentTypeFilter(opt);
              setCurrentPage(1);
            }}
            handleClick={handleContentTypeFilterClick}
            handleClose={handleContentTypeFilterClose}
            anchorEl={contentTypeFilterAnchorEl}
          />
        </div>
      ),
      renderCell: (it) => getContentTypeLabel(it.contentType, it.type),
    },
    {
      header: t('Date d\'ajout'),
      accessor: 'addedAt',
      width: '9%',
      renderCell: (it) => formatDate(it.addedAt),
    },
    {
      header: t('URL'),
      accessor: 'url',
      width: '13%',
      renderCell: (it) => (
        it.url ? (
          <div
            className="url-icon-container"
            onClick={(e) => {
              e.stopPropagation();
              handleTextClick(it);
            }}
          >
            <LinkIcon />
            <div className="url-tooltip">{it.url}</div>
          </div>
        ) : '-'
      ),
    },
    {
      header: t('Status'),
      accessor: 'status',
      width: '10%',
      renderHeader: () => (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span>{t('Status')}</span>
          <OrderTypeFilter
            orderTypeList={STATUS_FILTER_LIST}
            selectedType={selectedStatusFilter}
            setSelectedType={(opt) => {
              setSelectedStatusFilter(opt);
              setCurrentPage(1);
            }}
            handleClick={handleStatusFilterClick}
            handleClose={handleStatusFilterClose}
            anchorEl={statusFilterAnchorEl}
          />
        </div>
      ),
      renderCell: (it) => (
        <StatusDropdown
          textId={it.id}
          currentStatus={{
            delivered: it.delivered,
            validated: it.validated,
            deployed: it.deployed,
            inRevision: it.inRevision,
          }}
          design="iaSmart"
          onOptimisticUpdate={handleOptimisticUpdate}
          onStatusChange={handleStatusChange}
          onError={handleStatusChangeError}
          previousItem={it}
        />
      ),
    },
    {
      header: t('Livraison'),
      accessor: 'deliveryDate',
      width: '9%',
      renderCell: (it) => formatDate(it.deliveryDate),
    },
    {
      header: t('Validation'),
      accessor: 'validationDate',
      width: '8%',
      renderCell: (it) => formatDate(it.validationDate),
    },
    {
      header: t('Déploiement'),
      accessor: 'deploymentDate',
      width: '9%',
      renderCell: (it) => formatDate(it.deploymentDate),
    },
    {
      header: t('Action'),
      accessor: 'action',
      width: '11%',
      renderCell: (it) => (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleTextClick(it);
            }}
            title={t('Modifier')}
            style={{
              width: 32,
              height: 32,
              border: 'none',
              background: 'transparent',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderRadius: '4px',
            }}
          >
            <img src="Images/edit-icon.png" alt={t('Modifier')} style={{ width: 16, height: 16 }} />
          </button>
          <button
            type="button"
            onClick={(e) => handleActionsClick(e, it.id)}
            title={t('Plus d’actions')}
            data-actions-trigger="true"
            style={{
              width: 28,
              height: 28,
              border: 'none',
              background: 'transparent',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1,
              color: '#6B7280',
            }}
          >
            ...
          </button>
          {openActionsFor === it.id && (
            <div
              data-actions-menu="true"
              style={{
                position: 'fixed',
                top: actionsMenuCoords.top,
                left: actionsMenuCoords.left,
                backgroundColor: '#FFFFFF',
                boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
                borderRadius: 10,
                padding: '8px 0',
                minWidth: 190,
                zIndex: 1200,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                style={{
                  width: '100%',
                  padding: '6px 14px',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  fontSize: 13,
                  color: '#111827',
                  cursor: 'pointer',
                  display: 'block',
                }}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await DownloadTextWordType(it.id, user);
                  } catch (error) {
                    console.error('Erreur lors du téléchargement Word:', error);
                  } finally {
                    setOpenActionsFor(null);
                  }
                }}
              >
                {t('Telecharger le texte')}
              </button>
              <div
                style={{
                  borderTop: '1px solid #F3F4F6',
                  margin: '4px 0',
                }}
              />
              <button
                type="button"
                style={{
                  width: '90%',
                  padding: '6px 14px',
                  textAlign: 'left',
                  background: '#F1F1F1',
                  border: 'none',
                  fontSize: 13,
                  color: '#B91C1C',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  borderRadius: 6,
                  marginLeft: '5%',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(it);
                  setOpenActionsFor(null);
                }}
              >
                <img
                  src="/Images/icon-trash.png"
                  alt="delete"
                  style={{ width: 25, height: 25 }}
                />
                <span>{t('Supprimer')}</span>
              </button>
            </div>
          )}
        </div>
      ),
    },
  ]), [
    t,
    user,
    openActionsFor,
    actionsMenuCoords,
    CONTENT_TYPE_FILTER_LIST,
    STATUS_FILTER_LIST,
    selectedContentTypeFilter,
    selectedStatusFilter,
    contentTypeFilterAnchorEl,
    statusFilterAnchorEl,
  ]);

  useEffect(() => {
    if (!openActionsFor) return;
    const closeActions = (e) => {
      const target = e?.target;
      if (target?.closest?.('[data-actions-menu="true"]')) return;
      if (target?.closest?.('[data-actions-trigger="true"]')) return;
      setOpenActionsFor(null);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpenActionsFor(null);
    };
    document.addEventListener('mousedown', closeActions);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', closeActions);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openActionsFor]);

  // Fetch clients list — uniquement si jamais hydraté (null). Si [] la réponse API a déjà eu lieu.
  useEffect(() => {
    if (clients === null) {
      dispatch(fetchAdminClientClientList());
    }
  }, [dispatch, clients]);

  useEffect(() => {
    if (hasNoWebsiteConfigured) {
      setIsSearchOpen(false);
      setSearchTerm('');
      setCurrentPage(1);
    }
  }, [hasNoWebsiteConfigured]);

  useEffect(() => {
    const getWebsiteId = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const fromQuery = urlParams.get('websiteId');
      const fromQueryUrl = urlParams.get('url') || urlParams.get('urlSite');
      const fromStorage =
        localStorage.getItem('editWebsiteId') ||
        localStorage.getItem('websiteId') ||
        localStorage.getItem('selectedWebsiteId');

      if (fromQuery || fromStorage) return fromQuery || fromStorage;

      // Use selectedWebsiteUrl from the hook
      const rawList =
        clients !== null
          ? clients
          : (() => {
            try {
              const cached = localStorage.getItem('AC_Websites_Cache');
              if (cached) return JSON.parse(cached);
            } catch { }
            return [];
          })();

      const targetUrl = (fromQueryUrl || selectedWebsiteUrl || '').trim();
      if (targetUrl && rawList.length > 0) {
        const match = rawList.find((w) =>
          (w.urlSite || w.url || w.name || '').toLowerCase() === targetUrl.toLowerCase()
        );
        if (match?.id) return match.id;
      }

      return rawList?.[0]?.id || null;
    };

    const fetchTexts = async () => {
      if (clients === null) {
        setWebsiteId(null);
        setItems([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const currentId = await getWebsiteId();
        setWebsiteId(currentId);
        if (!currentId) {
          setItems([]);
          return;
        }

        if (lastWebsiteIdRef.current && lastWebsiteIdRef.current !== currentId) {
          setItems([]);
        }
        lastWebsiteIdRef.current = currentId;

        const { data } = await axiosPrivate.get(`/texts/external/by-website/${currentId}`);

        const arr = Array.isArray(data) ? data : data?.data || [];
        const mapped = arr.map((t) => {
          const flags = {
            delivered: !!t.delivered,
            validated: !!t.validated,
            deployed: !!t.deployed,
            inRevision: !!t.inRevision,
          };

          return {
            id: t.id || t._id || '',
            keyword: t.keyword || t.motCle || t.title || '',
            addedAt: t.createdAt || t.dateCreation || t.created_at || '',
            url: t.url || t.pageUrl || t.landingPage || '',
            status: resolveTextStatus(flags),
            deliveryDate: t.deliveredAt || t.deliveryDate || t.dateLivraison || '',
            validationDate: t.validateAt || t.validatedAt || t.validationDate || t.dateValidation || '',
            deploymentDate: t.deployedAt || t.deploymentDate || t.dateDeploiement || t.publicationDate || '',
            contentType: t.contentType || t.content_type || null,
            type: t.type || null,
            projectId: t.projectId || null,
            ...flags,
            revisionSent: !!t.revisionSent,
            globalRevisionSent: !!t.globalRevisionSent,
            revisionDone: !!t.revisionDone,
          };
        });
        setItems(mapped);
      } catch (e) {
        console.error('Erreur lors du chargement des textes du site:', e);
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTexts();

    const handleMaybeWebsiteChange = () => {
      fetchTexts();
    };

    window.addEventListener('popstate', handleMaybeWebsiteChange);
    window.addEventListener('hashchange', handleMaybeWebsiteChange);
    window.addEventListener('locationchange', handleMaybeWebsiteChange);
    window.addEventListener('storage', handleMaybeWebsiteChange);

    return () => {
      window.removeEventListener('popstate', handleMaybeWebsiteChange);
      window.removeEventListener('hashchange', handleMaybeWebsiteChange);
      window.removeEventListener('locationchange', handleMaybeWebsiteChange);
      window.removeEventListener('storage', handleMaybeWebsiteChange);
    };
  }, [selectedWebsiteUrl, clients, dispatch]);

  return (
    <div className="content-center" style={{ overflowX: 'hidden' }}>
      <style>{`
        .url-icon-container {
          position: relative;
          display: inline-flex;
          align-items: center;
          color: #6b7280;
          cursor: pointer;
        }

        .url-tooltip {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background-color: #6b7280;
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 12px;
          /* Retour à la ligne "normal" (par blocs) */
          white-space: normal;
          max-width: 420px;
          overflow-wrap: break-word;
          word-break: normal;
          z-index: 1000;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.2s ease, visibility 0.2s ease;
          margin-bottom: 8px;
          box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
        }

        .url-tooltip::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: #6b7280;
        }

        .url-icon-container:hover .url-tooltip {
          opacity: 1;
          visibility: visible;
        }
      `}</style>
      <style>{`
        body,
        .wrapper-content,
        .app-shell {
          background-color: #F6F6F6 !important;
        }

        .box-info-header,
        .box-input-text,
        .box-input-text > div,
        .box-input-text > div:focus,
        .box-input-text > div:active,
        .box-input-text input,
        .box-input-text input:focus,
        .box-input-text input:active {
          background-color: #F6F6F6 !important;
        }

        .box-wrapper,
        .table-responsive,
        .table-lists,
        .table-commandes,
        .texts-table,
        .texts-table thead,
        .texts-table tbody,
        .texts-table tr,
        .texts-table th,
        .texts-table td {
          background-color: #F6F6F6 !important;
        }

        .texts-table th,
        .texts-table td {
          width: calc(100% / 9);
        }

        .texts-table th {
          white-space: nowrap;
        }
      `}</style>
      <div className="box-info-header">
        <div className="b-child-1">
          <h1 className="mb-0" style={{ fontSize: 30, fontWeight: 600, color: '#3c3c3c' }}>{t('Mes contenus')}</h1>
        </div>
        <div className="b-child-2 d-flex justify-content-end align-items-center" style={{ gap: 12 }}>
          {!hasNoWebsiteConfigured && (
            <>
              {!isSearchOpen && (
                <button
                  type="button"
                  onClick={() => {
                    try {
                      const btn = document.querySelector('.website-texts-excel-wrapper .excel-export-button');
                      if (btn) btn.click();
                    } catch (e) { }
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#FFFFFF',
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#111827',
                    cursor: 'pointer',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  <img
                    src="/Images/icon-download-simple.png"
                    alt="download texts"
                    style={{ width: 14, height: 14 }}
                  />
                  <span>{t('Téléchargez tous les textes.')}</span>
                </button>
              )}
              {!isSearchOpen ? (
                <img
                  src="/Images/search-icon.png"
                  alt="search"
                  onClick={() => setIsSearchOpen(true)}
                  style={{
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                  }}
                />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    maxWidth: '320px',
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src="/Images/search-icon2.png"
                      alt="search"
                      style={{
                        position: 'absolute',
                        left: '10px',
                        width: '16px',
                        height: '16px',
                        pointerEvents: 'none',
                      }}
                    />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder={t('Rechercher un texte...')}
                      style={{
                        width: '100%',
                        padding: '8px 36px 8px 34px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                    style={{
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: '#111827',
                    }}
                  >
                    {t('Fermer')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {hasNoWebsiteConfigured && (
        <div className="website-texts-help-banner" role="note" style={{ marginBottom: 12 }}>
          <div className="website-texts-help-banner-left">
            <span className="website-texts-help-banner-text">
              {t('Aucun contenu disponible — veuillez d’abord ajouter un siteweb.')}
            </span>
          </div>
          <div className="website-texts-help-banner-actions">
            <button
              type="button"
              onClick={handleGoToAddWebsite}
              className="website-texts-help-banner-link"
            >
              {t('Ajouter un siteweb')}
            </button>
          </div>
        </div>
      )}

      <div className="website-texts-excel-wrapper" style={{ display: 'none' }}>
        <ExcelExportButton
          data={getFilteredDataForExport()}
          columns={exportColumns}
          fileName={`textes_website_${websiteId || 'selection'}_${new Date().toISOString().slice(0, 10)}.xlsx`}
        />
      </div>
      <DataTable
        headerClassName="ai-smart-table-head"
        columns={columns}
        data={paginatedItems}
        onRowClick={(row) => row?.id && handleTextClick(row)}
        emptyStateContent={
          <div
            style={{
              textAlign: 'center',
              padding: '12px 8px',
              backgroundColor: '#F6F6F6',
              borderRadius: '6px',
              margin: '6px 0',
              width: '100%',
              boxSizing: 'border-box',
              color: '#666',
              fontSize: 14,
              fontWeight: 400,
            }}
          >
            {isLoading
              ? t('Chargement...')
              : (items.length === 0 ? t('Aucun contenu.') : t('Aucun contenu pour ce filtre.'))}
          </div>
        }
      />

      {(() => {
        if (filteredItems.length === 0) return null;
        return (
          <TablePagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disablePrev={safeCurrentPage === 1}
            disableNext={safeCurrentPage === totalPages}
          />
        );
      })()}

      <ConfirmationModal
        open={confirmation}
        message={confirmationMsg}
        handleClose={() => {
          setConfirmation(false);
          setTextToDelete(null);
        }}
        executeAction={handleDeleteConfirm}
      />
    </div>
  );
}
